import type { FPRoom, FPPoint } from './FloorPlanEditor';

// Shared house-preset builder used by the House editor (panel cards) and the
// template picker (applied straight onto the Document). All presets are
// double-wall, every room has a door, plus width + height dimension arrows.

let _pid = 0;
const pid = () => 'p' + (++_pid) + '_' + Date.now().toString(36);
const rectPts = (x0: number, y0: number, x1: number, y1: number): FPPoint[] => [
  { x: Math.min(x0, x1), y: Math.min(y0, y1) }, { x: Math.max(x0, x1), y: Math.min(y0, y1) },
  { x: Math.max(x0, x1), y: Math.max(y0, y1) }, { x: Math.min(x0, x1), y: Math.max(y0, y1) },
];

export const HM = 0.05; // houseMap margin (must match the engine)

export type PlotGeom = { scale: number; bw: number; bh: number } | null;

// Compute geometry (feet→normalized scale) from a plot's canvas points + scale.
export function geomFromCanvas(canvasPts: { x: number; y: number }[] | undefined, scale: number | undefined): PlotGeom {
  if (!canvasPts || !canvasPts.length || !scale || scale <= 0) return null;
  const xs = canvasPts.map((p) => p.x), ys = canvasPts.map((p) => p.y);
  const bw = (Math.max(...xs) - Math.min(...xs)) * (1 - 2 * HM) || 1;
  const bh = (Math.max(...ys) - Math.min(...ys)) * (1 - 2 * HM) || 1;
  return { scale, bw, bh };
}

export function buildHousePreset(kind: string, geom: PlotGeom): { rooms: FPRoom[]; wall: 'double'; fields?: Record<string, unknown>; plot?: { n: number; s: number; e: number; w: number } } {
  const BX0 = 0.14, BY0 = 0.10, BX1 = 0.86, BY1 = 0.78, G = 0.05;
  const room = (type: string, x0: number, y0: number, x1: number, y1: number, label: string, doorWall = 2): FPRoom =>
    ({ id: pid(), type, label, points: rectPts(x0, y0, x1, y1), rect: true, ...(type === 'stairs' ? {} : { doors: [{ id: pid(), wall: doorWall, pos: 0.5, width: 0.26 }] }) });
  const useFt = !!geom;
  const bw = geom ? geom.bw : 1, bh = geom ? geom.bh : 1;
  const ftX = (f: number) => Math.min(0.84, useFt ? (f * geom!.scale) / bw : f * 0.024);
  const ftY = (f: number) => Math.min(0.84, useFt ? (f * geom!.scale) / bh : f * 0.024);
  const ovX = useFt ? 3.2 / bw : 0.008, ovY = useFt ? 3.2 / bh : 0.008;
  const hx = ovX / 2, hy = ovY / 2;
  let rs: FPRoom[] = [];
  if (kind === 'single') {
    const w = ftX(10), h = ftY(10), cx = 0.5, cy = 0.44;
    rs = [room('living', cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2, 'ROOM')];
  } else if (kind === 'double') {
    const w = ftX(10), h = ftY(10), totalW = 2 * w - ovX;
    const x0 = 0.5 - totalW / 2, cy = 0.44, y0 = cy - h / 2, y1 = cy + h / 2;
    rs = [room('living', x0, y0, x0 + w, y1, 'ROOM 1'), room('living', x0 + w - ovX, y0, x0 + 2 * w - ovX, y1, 'ROOM 2')];
  } else if (kind === 'twoBedHall') {
    const mx = (BX0 + BX1) / 2, my = BY0 + (BY1 - BY0) * 0.46;
    rs = [
      room('bedroom', BX0, BY0, mx + hx, my + hy, 'BEDROOM 1'),
      room('bedroom', mx - hx, BY0, BX1, my + hy, 'BEDROOM 2'),
      room('hall', BX0, my - hy, BX1, BY1, 'HALL'),
    ];
  } else if (kind === 'houseStairs') {
    const mx = BX0 + (BX1 - BX0) * 0.56, my = BY0 + (BY1 - BY0) * 0.5;
    rs = [
      room('living', BX0, BY0, mx + hx, BY1, 'LIVING'),
      room('stairs', mx - hx, BY0, BX1, my + hy, 'STAIRCASE'),
      room('bedroom', mx - hx, my - hy, BX1, BY1, 'BEDROOM'),
    ];
  } else if (kind === 'threeRoom') {
    const t = (BX1 - BX0) / 3;
    rs = [
      room('living', BX0, BY0, BX0 + t + hx, BY1, 'ROOM 1'),
      room('living', BX0 + t - hx, BY0, BX0 + 2 * t + hx, BY1, 'ROOM 2'),
      room('living', BX0 + 2 * t - hx, BY0, BX1, BY1, 'ROOM 3'),
    ];
  } else if (kind === 'oneBHK') {
    const mx = BX0 + (BX1 - BX0) * 0.55, my = BY0 + (BY1 - BY0) * 0.5;
    rs = [
      room('bedroom', BX0, BY0, mx + hx, my + hy, 'BEDROOM'),
      room('kitchen', mx - hx, BY0, BX1, my + hy, 'KITCHEN'),
      room('hall', BX0, my - hy, BX1, BY1, 'HALL'),
    ];
  } else if (kind === 'twoBHK') {
    const mx = (BX0 + BX1) / 2, my = BY0 + (BY1 - BY0) * 0.5, kx = BX0 + (BX1 - BX0) * 0.62;
    rs = [
      room('bedroom', BX0, BY0, mx + hx, my + hy, 'BEDROOM 1'),
      room('bedroom', mx - hx, BY0, BX1, my + hy, 'BEDROOM 2'),
      room('hall', BX0, my - hy, kx + hx, BY1, 'HALL'),
      room('kitchen', kx - hx, my - hy, BX1, BY1, 'KITCHEN'),
    ];
  } else if (kind === 'house2bhk') {
    // Exact layout captured from a refined saved design: two bedrooms + two
    // toilets on top, hall across the middle, kitchen + drawing below, stairs.
    // Per-room label sizes (rooms 0.85, toilets 0.7) are retained as tuned.
    const mk = (type: string, x0: number, y0: number, x1: number, y1: number, label: string, doors?: [number, number][], ls?: number): FPRoom =>
      ({ id: pid(), type, label, rect: true, points: rectPts(x0, y0, x1, y1), ...(ls ? { labelScale: ls } : {}), ...(doors ? { doors: doors.map((d) => ({ id: pid(), wall: d[0], pos: d[1], width: 0.26 })) } : {}) });
    rs = [
      mk('bedroom', 0.18, 0.09, 0.5085, 0.3751, 'BEDROOM 1', [[2, 0.5]], 0.85),
      mk('toilet', 0.4915, 0.09, 0.6684, 0.1851, 'TOILET', [[3, 0.558]], 0.7),
      mk('toilet', 0.6515, 0.09, 0.82, 0.1851, 'TOILET', [[2, 0.5]], 0.7),
      mk('bedroom', 0.4915, 0.1749, 0.82, 0.3751, 'BEDROOM 2', [[2, 0.5]], 0.85),
      mk('hall', 0.18, 0.3649, 0.82, 0.5351, 'HALL', [[2, 0.851]], 0.85),
      mk('kitchen', 0.18, 0.5249, 0.5085, 0.7151, 'KITCHEN', [[0, 0.690]], 0.85),
      mk('living', 0.4915, 0.5249, 0.82, 0.7151, 'DRAWING', [[2, 0.716]], 0.85),
      mk('stairs', 0.20, 0.74, 0.44, 0.83, 'STAIRCASE'),
    ];
  } else if (kind === 'house1bhk') {
    // Exact layout captured from a refined saved design: bedroom + kitchen down
    // the left, two toilets top-centre, a large drawing/living room on the
    // right, staircase below. Doors keep their tuned walls + positions.
    const mk = (type: string, x0: number, y0: number, x1: number, y1: number, label: string, doors?: [number, number][]): FPRoom =>
      ({ id: pid(), type, label, rect: true, points: rectPts(x0, y0, x1, y1), ...(doors ? { doors: doors.map((d) => ({ id: pid(), wall: d[0], pos: d[1], width: 0.26 })) } : {}) });
    rs = [
      mk('bedroom', 0.18, 0.10, 0.5085, 0.4251, 'BEDROOM 1', [[1, 0.78]]),
      mk('kitchen', 0.18, 0.4149, 0.5085, 0.70, 'KITCHEN', [[1, 0.687]]),
      mk('toilet', 0.4915, 0.10, 0.6685, 0.1951, 'TOILET', [[3, 0.538]]),
      mk('toilet', 0.6515, 0.10, 0.82, 0.1951, 'TOILET', [[2, 0.5]]),
      mk('living', 0.4915, 0.184, 0.8193, 0.70, 'DRAWING', [[2, 0.5], [1, 0.86]]),
      mk('stairs', 0.20, 0.73, 0.44, 0.82, 'STAIRCASE'),
    ];
  } else if (kind === 'house3bhk') {
    // Exact 3-bedroom design (frozen): 3 bedrooms + kitchen, an L-shaped merged
    // HALL (two grouped rects), three toilets and a staircase.
    const gid = 'g' + pid();
    const mk = (type: string, x0: number, y0: number, x1: number, y1: number, label: string, doors?: [number, number][], group?: string): FPRoom =>
      ({ id: pid(), type, label, rect: true, points: rectPts(x0, y0, x1, y1), ...(group ? { group } : {}), ...(doors ? { doors: doors.map((d) => ({ id: pid(), wall: d[0], pos: d[1], width: 0.26 })) } : {}) });
    rs = [
      mk('bedroom', 0.096, 0.0702, 0.3063, 0.3333, 'BEDROOM 1', [[1, 0.7482]]),
      mk('kitchen', 0.0957, 0.4515, 0.3072, 0.708, 'KITCHEN', [[1, 0.2227]]),
      mk('hall', 0.2968, 0.0702, 0.5494, 0.7081, 'HALL', undefined, gid),
      mk('toilet', 0.6428, 0.0702, 0.7535, 0.3295, 'TOILET', [[1, 0.7631]]),
      mk('bedroom', 0.7431, 0.0702, 0.9436, 0.4265, 'BEDROOM 2', [[3, 0.8471]]),
      mk('bedroom', 0.7423, 0.4134, 0.9441, 0.7081, 'BEDROOM 3', [[3, 0.2318], [2, 0.5]]),
      mk('toilet', 0.5376, 0.0702, 0.6528, 0.3299, 'TOILET', [[3, 0.6825]]),
      mk('toilet', 0.096, 0.3242, 0.3063, 0.4638, 'TOILET', [[0, 0.7609]]),
      mk('stairs', 0.096, 0.7081, 0.3344, 0.848, 'STAIRCASE'),
      mk('rect', 0.5382, 0.3174, 0.7524, 0.7081, 'HALL', [[2, 0.5708]], gid),
    ];
  } else if (kind === 'house2bhkB') {
    // Alternative 2 BHK captured from a refined design: kitchen/puja + an
    // L-shaped merged HALL down the left/centre, master + child bedrooms with two
    // toilets on the right, staircase far left. The hall is three grouped rects.
    const gid = 'g' + pid();
    const mk = (type: string, x0: number, y0: number, x1: number, y1: number, label: string, doors?: [number, number][], group?: string): FPRoom =>
      ({ id: pid(), type, label, rect: true, points: rectPts(x0, y0, x1, y1), ...(group ? { group } : {}), ...(doors ? { doors: doors.map((d) => ({ id: pid(), wall: d[0], pos: d[1], width: 0.26 })) } : {}) });
    rs = [
      mk('kitchen', 0.166, 0.0902, 0.4456, 0.3505, 'KITCHEN', [[1, 0.6607]]),
      mk('puja', 0.166, 0.3386, 0.4456, 0.5474, 'PUJA', [[1, 0.5338]]),
      mk('living', 0.166, 0.5348, 0.4456, 0.7844, 'HALL / LIVING', [[3, 0.75], [2, 0.4145]], gid),
      mk('hall', 0.4362, 0.0894, 0.726, 0.7844, 'HALL / LIVING', undefined, gid),
      mk('rect', 0.7161, 0.3446, 0.8296, 0.5485, 'HALL / LIVING', undefined, gid),
      mk('bedroom', 0.7161, 0.0886, 0.984, 0.3565, 'M.BEDROOM', [[2, 0.2256]]),
      mk('bedroom', 0.7161, 0.5361, 0.9843, 0.7844, 'C.BEDROOM', [[0, 0.2216]]),
      mk('toilet', 0.8197, 0.344, 0.9849, 0.4523, 'TOILET', [[0, 0.2826]]),
      mk('toilet', 0.8197, 0.4403, 0.9847, 0.5476, 'TOILET', [[2, 0.2583]]),
      mk('stairs', 0.0635, 0.0968, 0.166, 0.4403, 'STAIRCASE'),
    ];
  } else if (kind === 'studio') {
    rs = [room('living', BX0, BY0, BX1, BY1, 'STUDIO')];
  } else {
    const w = ftX(10), h = ftY(10), cx = 0.5, cy = 0.44;
    rs = [room('living', cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2, 'ROOM')];
  }
  // Width + height dimension arrows wrapping the actual layout bbox.
  const rxs = rs.flatMap((r) => r.points.map((p) => p.x)), rys = rs.flatMap((r) => r.points.map((p) => p.y));
  const lx0 = Math.min(...rxs), lx1 = Math.max(...rxs), ly0 = Math.min(...rys), ly1 = Math.max(...rys);
  const dims: FPRoom[] = [
    { id: pid(), type: 'dim', label: '', points: [{ x: lx0, y: ly1 + G }, { x: lx1, y: ly1 + G }] },
    { id: pid(), type: 'dim', label: '', points: [{ x: lx0 - G, y: ly0 }, { x: lx0 - G, y: ly1 }] },
  ];
  // Presets that freeze a specific compass orientation (South-up for the local
  // registration format) and plot size pass them along so applying the preset
  // also sets them.
  // Presets that freeze the full page setup (plot size, compass, tuned scale +
  // position, and the moved references block) so the plot fills the page exactly
  // as designed. The shared placement is reused; only the compass differs.
  const PLACEMENT = { flexFixedScale: 5.700616, plotPosOffset: { x: 55.232, y: -35.985 }, blockOffsets: { area: { x: 33.892, y: 10.042 } } };
  const FROZEN: Record<string, { fields: Record<string, unknown>; plot: { n: number; s: number; e: number; w: number } }> = {
    house2bhkB: { fields: { compass_top: 'S', ...PLACEMENT }, plot: { n: 60, s: 60, e: 50, w: 50 } },
    house3bhk: { fields: { compass_top: 'W', ...PLACEMENT }, plot: { n: 60, s: 60, e: 50, w: 50 } },
  };
  return { rooms: [...rs, ...dims], wall: 'double', fields: FROZEN[kind]?.fields, plot: FROZEN[kind]?.plot };
}
